import { formatKg } from '@common/utils/formaters';
import { observer } from 'mobx-react';
import React, { FC, useCallback, useMemo } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { makeStyles } from '~/@components/@theme';
import { Button } from '~/@components/Button';
import { LocalizableText } from '~/@components/LocalizableText';
import { DashboardTitle, SimpleDashboardToolTip } from '~/@views/UserView/ProjectPage/ProjectDashboard/common';
import { dashboardFilterStore } from '~/@views/UserView/ProjectPage/ProjectDashboard/dashboardFilter.store';
import { ProjectQuery_project } from '~/graphql';
import i18n from '~/i18n';
import { downloadProjectDriversCO2Xlsx } from '~/utils/project';

import { COLOR } from '../dashboard.theme';

type Props = {
    project: ProjectQuery_project;
};

export const ProjectCO2TrucksDashboard: FC<Props> = observer(({ project }) => {
    const { chart, header } = useStyles();

    const onDownloadReport = useCallback(() => {
        const ignoredPromise = downloadProjectDriversCO2Xlsx(project.id, project.name);
    }, [project]);

    const series = useMemo(() => {
        const mapDeliveries = dashboardFilterStore.filteredDeliveryItems.reduce((acc, cur) => {
            const licensePlate = cur.licensePlate;
            if (!acc.has(licensePlate)) acc.set(licensePlate, 0);

            acc.set(licensePlate, acc.get(licensePlate)! + cur.CO2Consumption);

            return acc;
        }, new Map<string, number>());

        const map = dashboardFilterStore.filteredTimeSpentItems
            .filter(i => !!i.driver)
            .reduce((acc, cur) => {
                const licensePlate = cur.licensePlate;
                if (!acc.has(licensePlate)) acc.set(licensePlate, 0);

                acc.set(licensePlate, acc.get(licensePlate)! + cur.CO2Consumption);

                return acc;
            }, mapDeliveries);

        const keys = Array.from(map.keys()).sort();

        return keys.map(k => ({ licensePlate: k, CO2: map.get(k)! }));
    }, [dashboardFilterStore.filteredDeliveryItems, dashboardFilterStore.filteredTimeSpentItems]);

    return (
        <div className={chart}>
            <div className={header}>
                <DashboardTitle>
                    <LocalizableText code={'ProjectDashboard.titleCO2Trucks'} />
                </DashboardTitle>
            </div>
            <ResponsiveContainer height={220}>
                <BarChart data={series}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="licensePlate" />
                    <YAxis />
                    <Tooltip
                        content={
                            <SimpleDashboardToolTip
                                labelFormatter={v => `${i18n.ProjectDashboard.regNumber}: ${v}`}
                                labelKey="licensePlate"
                                valueKey="CO2"
                                valueLabel="CO2"
                                valueFormatter={formatKg}
                            />
                        }
                        shared={false}
                    />
                    <Bar type="monotone" dataKey="CO2" fill={COLOR.primaryStartColor} />
                </BarChart>
            </ResponsiveContainer>
            <Button color="primary" variant="outlined" onClick={onDownloadReport}>
                <LocalizableText code={'DriversCO2Xlsx.exportToXlsx'} />
            </Button>
        </div>
    );
});

const useStyles = makeStyles(theme => ({
    chart: {
        height: 'calc(50vh - 160px)',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        alignItems: 'center',
        [theme.breakpoints.down('md')]: {
            height: 'auto',
        },
    },
    header: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        flexWrap: 'wrap',
        gap: 8,
    },
}));
